/**
 * RFC-194 — Unified Trigger Model.
 *
 * Everything that causes a runbook to run is a Trigger. A Trigger has a `kind`
 * (SYSTEM | USER) and a `type` (MANUAL | CRON | EVENT | CADENCE), and every
 * trigger stamps a typed `TriggerCause` onto the execution it starts so the run
 * records *why* it began.
 *
 * Three of the four types (MANUAL, EVENT, CADENCE) share one dispatch primitive
 * (`startRunbookFromTrigger`); CRON (`ScheduledExecution`) is taxonomy-only — it
 * participates in the provenance model but keeps its own wall-clock execution
 * path (§0/§3.1).
 *
 * Provenance is persisted on `WorkflowExecution.params.triggerCause` (NOT through
 * runbook `inputs`, which drops undeclared keys — §3.2). It is consumed to
 * suppress CES-status side-effects for system-fired (CADENCE/EVENT) runs so a
 * machine never advances the compliance lifecycle behind a human (§3.6).
 */

export type TriggerKind = 'SYSTEM' | 'USER';

export type TriggerType = 'MANUAL' | 'CRON' | 'EVENT' | 'CADENCE' | 'CHECK';

/** Typed provenance stamped onto every started execution. */
export type TriggerCause =
  | { type: 'MANUAL'; userId: string }
  | { type: 'CRON'; scheduledExecutionId: string }
  | { type: 'EVENT'; triggerId: string; eventType: string; subjectId?: string }
  | {
      type: 'CADENCE';
      cesId: string;
      nextRetestAt: string;
      reason: 'retest_due';
    }
  // A bound procedure auto-run on its own check frequency (`checkFrequencyDays`),
  // independent of the test's retest cadence. Like CADENCE it pre-collects
  // evidence and MUST NOT advance the CES (a human still submits).
  | {
      type: 'CHECK';
      cesId: string;
      checkFrequencyDays: number | null;
    };

/**
 * A system trigger whose run produces evidence but MUST NOT advance the CES
 * compliance lifecycle (RFC-194 §3.6). `EVENT`, `CADENCE`, and `CHECK` qualify:
 *  - `MANUAL` is a user-initiated run and advances CES status as it does today.
 *  - `CRON` runs its own non-CES path and never reaches the CES side-effect.
 *  - `CHECK` is a bound procedure's scheduled re-run — evidence only.
 *
 * Used at every `updateLinkedTestStatus` call site to no-op the CES write for a
 * system-fired run.
 */
export function isSystemTriggerCause(
  cause: TriggerCause | null | undefined,
): boolean {
  return (
    cause?.type === 'EVENT' ||
    cause?.type === 'CADENCE' ||
    cause?.type === 'CHECK'
  );
}

// ── Input bindings (RFC-194 §8.7, revised) ──────────────────────────────────
//
// A runbook stays domain-agnostic: it declares ordinary params (`@params.*`) and
// never reaches into trigger-specific context. The BINDING (event trigger /
// scheduled check) is what fills those params, per param, from one source:
//  - `fixed` — a constant typed at bind time.
//  - `event` — a dot-path into the EVENT envelope (`subjectId` / `payload.<f>`);
//    resolved per fire. EVENT bindings only.
//  - `ces`   — a CES variable (static per binding). SCHEDULE/CADENCE bindings only.
// Resolved to a concrete `inputs` object when the run fires (no magic namespace).

export type InputBindingSource = 'fixed' | 'event' | 'ces';

export type InputBinding =
  | { source: 'fixed'; value: unknown }
  | { source: 'event'; path: string }
  | { source: 'ces'; variable: string };

/** `{ runbookParamName: InputBinding }`. */
export type InputBindingMap = Record<string, InputBinding>;

/** A CES field a scheduled/cadence check can bind a runbook param to. Static per
 *  binding (known at bind time) — the reason CES needs no runtime namespace. */
export interface CesBindingVariable {
  variable: string;
  label: string;
  /** Shape hint for the UI / consumer (controlIds is a list). */
  type: 'string' | 'string[]';
}

export const CES_BINDING_VARIABLES: CesBindingVariable[] = [
  { variable: 'cesId', label: 'Test ID', type: 'string' },
  { variable: 'controlIds', label: 'Control IDs', type: 'string[]' },
  { variable: 'workspaceId', label: 'Workspace ID', type: 'string' },
  { variable: 'title', label: 'Test title', type: 'string' },
  { variable: 'taskCode', label: 'Task code', type: 'string' },
  { variable: 'nextRetestAt', label: 'Next retest date', type: 'string' },
];

export const CES_BINDING_VARIABLE_NAMES = CES_BINDING_VARIABLES.map(
  (v) => v.variable,
);

export function cesBindingVariableLabel(variable: string): string {
  return (
    CES_BINDING_VARIABLES.find((v) => v.variable === variable)?.label ?? variable
  );
}

// ── Runbook diagram authoring config (RFC-194 UI) ───────────────────────────
//
// These describe how a *single runbook* is configured to start, and what side
// notifications it fans out — the visual "start node" + notification nodes in
// the runbook graph editor. They are the editor's data contract; durable
// storage maps per-type onto the existing surfaces (EVENT → ProcedureEventTrigger,
// CRON → ScheduledExecution, CADENCE → CES retest cadence) per §3.1.

import type { ProcedureEventFilterPredicate } from './procedure-event-trigger.dto';

/** The runbook's start configuration, discriminated by trigger `type`. */
export type RunbookTriggerDTO =
  | { type: 'MANUAL' }
  | { type: 'CRON'; schedule: string; timezone?: string }
  | { type: 'EVENT'; eventType: string; filter?: ProcedureEventFilterPredicate[] }
  | { type: 'CADENCE'; cadenceDays: number };

// ── RFC-194 §8.3 / §8.5 F2 — trigger binding list ───────────────────────────
//
// A trigger is an N:1, per-consumer *binding* that points at a (shared) runbook,
// not a node in its graph. The runbook editor's start node reads these from the
// canonical per-type tables (ProcedureEventTrigger / ScheduledExecution /
// per-CES cadence) and writes through their existing routers — it never folds a
// trigger into the runbook definition.

export interface RunbookEventBindingDTO {
  id: string;
  eventType: string;
  eventFilter: ProcedureEventFilterPredicate[] | null;
  controlEvidenceStatusId: string | null;
  enabled: boolean;
}

export interface RunbookScheduleBindingDTO {
  id: string;
  /** Cron expression. */
  schedule: string;
  timezone: string;
  enabled: boolean;
}

/** A test this runbook is bound to as its evidence tool, with its "Run every"
 *  frequency (`checkFrequencyDays`) — the runbook check sweep re-runs it on that
 *  cadence. Distinct from the test's retest cadence (human re-attestation). */
export interface RunbookCadenceBindingDTO {
  cesId: string;
  title: string;
  taskCode: string | null;
  status: string;
  /** Interval schedule ("Run every N days"); null when a cron is used instead. */
  checkFrequencyDays: number | null;
  /** Day-granular "Custom" cron schedule; null when an interval is used instead. */
  checkCron: string | null;
}

/** The 0..N triggers that target one runbook, aggregated from their per-type tables. */
export interface RunbookTriggerBindingsDTO {
  events: RunbookEventBindingDTO[];
  schedules: RunbookScheduleBindingDTO[];
  cadences: RunbookCadenceBindingDTO[];
}

export type RunbookNotificationChannel = 'slack' | 'email' | 'teams';

/** When in the run lifecycle a notification fires. */
export type RunbookNotificationEvent = 'on_start' | 'on_complete' | 'on_failure';

/** A side-effect notification node attached to a runbook (Slack/Email/Teams). */
export interface RunbookNotificationDTO {
  id: string;
  channel: RunbookNotificationChannel;
  /** Channel/recipient: Slack channel (e.g. `#compliance`), email address(es), or Teams channel/webhook. */
  target: string;
  /** Message template; supports `@params.<name>` interpolation. */
  message?: string;
  /** Lifecycle point at which to send. */
  event: RunbookNotificationEvent;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Read the persisted `TriggerCause` off a `WorkflowExecution.params` JSON blob.
 * Returns `null` for legacy/manual executions that carry no provenance. Tolerant
 * by design — a malformed blob is treated as "no cause" rather than throwing.
 */
export function readPersistedTriggerCause(
  params: unknown,
): TriggerCause | null {
  if (!isRecord(params)) return null;
  const cause = params.triggerCause;
  if (isRecord(cause) && typeof cause.type === 'string') {
    return cause as unknown as TriggerCause;
  }
  return null;
}
